import React, { useCallback, useEffect, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { VideoPlayerQuestion } from '../../questions/VideoPlayerQuestion';
import { QuestionStatusLine } from '../../questions/QuestionStatusLine';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const VideoPlayerRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  const questionId = question?.id ?? existingResponse?.question ?? -1;
  const [isValid, setIsValid] = useState<boolean>(Boolean(existingResponse));
  // The learner chose to continue because the video would not play.
  const [skipped, setSkipped] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    setIsValid(Boolean(existingResponse));
  }, [existingResponse, questionId]);

  useEffect(() => {
    setSkipped(false);
  }, [questionId]);

  useEffect(() => {
    const canContinue = isValid || skipped;
    onHasUserInputChange?.(canContinue);
    onValidityChange?.(canContinue);
    onExtrasChange?.({
      isVideoValid: canContinue,
    });

    if (questionId < 0) {
      onDraftChange(null);
      return;
    }

    if (isValid) {
      onDraftChange(existingResponse ?? { question: questionId });
    } else if (skipped) {
      const skippedPayload: ResponsePayload = {
        question: questionId,
        metadata: { video_unavailable: true },
      };
      onDraftChange(skippedPayload);
    } else {
      onDraftChange(null);
    }
  }, [
    existingResponse,
    isValid,
    skipped,
    onDraftChange,
    onExtrasChange,
    onHasUserInputChange,
    onValidityChange,
    questionId,
  ]);

  const handleValidationChange = useCallback((nextValid: boolean) => {
    setIsValid(nextValid);
  }, []);

  const handleSkip = useCallback(() => setSkipped(true), []);

  return (
    <div className="w-full">
      <VideoPlayerQuestion
        question={question}
        onValidationChange={handleValidationChange}
        initiallyValid={Boolean(existingResponse)}
        onSkip={skipped ? undefined : handleSkip}
        onUnavailableChange={setIsUnavailable}
      />
      {skipped && isUnavailable ? (
        <QuestionStatusLine ready>Skipped for now. You can come back to this video later.</QuestionStatusLine>
      ) : null}
      {!isValid && !skipped && !isUnavailable ? (
        <QuestionStatusLine>Continue unlocks when the video ends.</QuestionStatusLine>
      ) : null}
    </div>
  );
};

registerQuestionRenderer(['video_player'], VideoPlayerRenderer);
